import React, { useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useDashboardProcess from "@/hooks/use-dashboard-process.ts";
import { fetchItem } from "@/queries/queries-helper.ts";
import ENDPOINTS, { INDICATORS_ENDPOINTS } from "@/services/endpoints.ts";

import { IndicatorsContext, ProjectIndicatorsContextProps } from "./project-indicators-context-props.ts";

import WorkspaceFilesDto from "@/models/interfaces/dtos/workspace-files-dto.ts";
import PathElementDto from "@/models/interfaces/dtos/path-element-dto.ts";
import ProjectIndicatorsDto from "@/models/interfaces/dtos/indicators/project-indicators-dto.ts";

const ProjectIndicatorsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { project } = useProjectContext();
    const { dashboardProcessStatusQuery } = useDashboardProcess(project.id);

    const queryClient = useQueryClient();

    const processData = dashboardProcessStatusQuery.data;
    const isProcessLoading = dashboardProcessStatusQuery.isLoading;

    const hasSurfaces = processData?.hasSurfaces === true || processData?.hasCalcul1 === true;
    const shouldFetchIndicators = !isProcessLoading && hasSurfaces;

    // Indicators
    const { data: projectIndicatorsData, isLoading: isProjectIndicatorsLoading } = useQuery(
        ['indicators-def', project.id],
        async () => fetchItem<ProjectIndicatorsDto>(
            INDICATORS_ENDPOINTS.GET_PROJECT_DEFINITIONS(
                project.id,
                ['SHO', 'SFN', 'MIN', 'TER', 'PAF', 'BLC', 'SHAB', 'SDP', 'ST', 'SHOB']
            )
        ),
        {
            enabled: shouldFetchIndicators
        }
    );

    // Files path
    const { data: filesPathData, isLoading: isFilesPathLoading } = useQuery(
        ['files-path', project.id],
        async () => {
            const data = await fetchItem<WorkspaceFilesDto>(
                ENDPOINTS.PROJECT_3D_BOOMER_FILES(project.id)
            );

            if (!data) return undefined;

            const filteredData: PathElementDto[] = [];

            data.workspaceFilesPaths.forEach(wp => {
                wp.filePaths.forEach(fp => {
                    filteredData.push(fp);
                });
            });

            return filteredData;
        },
        {
            enabled: shouldFetchIndicators,
            // Boomer can overwrite the geometry without changing any file path.
            // A successful refetch must therefore also refresh the viewer loaders.
            structuralSharing: false
        }
    );

    const invalidateProjectIndicatorsData = useCallback(async() => {
        await queryClient.invalidateQueries({queryKey: ['indicators-def', project.id]});
    }, [queryClient, project.id]);

    const invalidateFilesPathData = useCallback(async() => {
        await queryClient.invalidateQueries({queryKey: ['files-path', project.id]});
    }, [queryClient, project.id]);

    // Loading global
    const isIndicatorsFetching =
        isProjectIndicatorsLoading ||
        isFilesPathLoading;

    const isLoading =
        isProcessLoading ||
        (shouldFetchIndicators && isIndicatorsFetching);

    const contextProps: ProjectIndicatorsContextProps = {
        isLoading,
        projectIndicatorsData,

        filesPathData,

        invalidateProjectIndicatorsData,
        invalidateFilesPathData
    }

    return (
        <IndicatorsContext.Provider value={contextProps}>
            {children}
        </IndicatorsContext.Provider>
    )
}

export default ProjectIndicatorsProvider;
